export function isValidHttpUrl(url: string) {
  const validUrl = normalizeUrlIncludingProtocolRelative(url);
  if (!validUrl) {
    return false;
  }
  return validUrl.protocol === "http:" || validUrl.protocol === "https:";
}

/**
 * Tries to parse and validate the given string as a URL, including protocol-relative
 * URLs. Returns a URL if parsing is possible, otherwise null.
 *
 * Note: Javascript URLs don't recognize protocol-relative URLs (e.g. //google.com)
 * as valid, so we need special casing here. If protocol-relative, the returned
 * url will have an https protocol. This default is arbitrary but backed up by best
 * practice: https://webhint.io/docs/user-guide/hints/hint-no-protocol-relative-urls/
 */
export function normalizeUrlIncludingProtocolRelative(url: string): URL | null {
  let validUrl;
  try {
    validUrl = new URL(url);
  } catch {
    if (url.startsWith("//")) {
      try {
        validUrl = new URL(`https:${url}`);
      } catch {
        return null;
      }
    } else {
      return null;
    }
  }
  return validUrl;
}

/**
 * Is the value a valid domain including subdomain + TLDs, not accounting for
 * real, recognized TLDs. Ie. we only make sure that the TLD contains at least
 * two alphanumeric characters. Additionally, this is case insensitive.
 */
export function isValidDomain(domain: string): boolean {
  const domainRegex =
    /^(?:[\dA-Za-z](?:[\dA-Za-z-]*[\dA-Za-z])?\.)+[A-Za-z]{2,}$/;
  return domainRegex.test(domain);
}

export function normalizeUrlScheme(url: string) {
  return url.startsWith("//") ? `https:${url}` : url;
}

/**
 * Truncates a domain name by shortening the subdomain portion while preserving
 * the main domain suffix. Useful for displaying long domain names in UI.
 *
 * @param domain - The domain to truncate (e.g., "very-long-subdomain.example.com")
 * @param maxLength - Maximum length of the truncated domain (default: 40)
 * @returns Truncated domain with ellipsis (e.g., "very-long-sub...example.com")
 */
export function truncateDomain(domain: string, maxLength = 60): string {
  // If domain is short enough, return as-is
  if (domain.length <= maxLength) {
    return domain;
  }

  // Split the domain into parts
  const parts = domain.split(".");

  // Need at least 2 parts (subdomain.domain)
  if (parts.length < 2) {
    return domain.length <= maxLength
      ? domain
      : `${domain.slice(0, maxLength - 3)}...`;
  }

  // For common patterns like *.reploshops.com, preserve the last 2 parts
  // For regular domains, preserve the last 2 parts (domain.tld)
  const mainDomainParts = parts.slice(-2);
  const subdomainParts = parts.slice(0, -2);

  const mainDomain = mainDomainParts.join(".");

  // If even just the main domain is too long, truncate it
  if (mainDomain.length >= maxLength - 3) {
    return `${mainDomain.slice(0, maxLength - 3)}...`;
  }

  // Calculate available space for subdomain
  const availableSpace = maxLength - mainDomain.length - 4; // 4 for "..." and "."

  if (subdomainParts.length === 0) {
    return domain;
  }

  const subdomainString = subdomainParts.join(".");

  if (subdomainString.length <= availableSpace) {
    return domain;
  }

  // Truncate subdomain portion
  const truncatedSubdomain = subdomainString.slice(
    0,
    Math.max(1, availableSpace),
  );

  return `${truncatedSubdomain}...${mainDomain}`;
}
